/* eslint-disable max-lines */
import { z } from 'zod';

import { ZodId } from '@Common/types/request';
import { createZodArrayable } from '@Common/types/zod/zod-arrayable';
import { ZodSecureEmail } from '@Common/types/zod/zod-email';
import { endpoint, type GetEndpointType } from '@Common/types/zod/zod-endpoint';
import { ZodTrimmedString } from '@Common/types/zod/zod-trimmed-string';

export const adminGroupName = 'Backoffice Super Admin';
export const readOnlyAdminGroupName = 'Backoffice Read Only Admin';
export const adminGroupEmail = 'bo-super-admin@shares.io';
export const externalAdminGroupEmail = 'bo-super-admin@external-shares.io';
export const readOnlyAdminGroupEmail = 'bo-read-only-admin@shares.io';

export const backofficeGroupsLevel1 = [
  'bo-product-level-1@shares.io',
  'bo-operations-level-1@shares.io',
  'bo-compliance-level-1@shares.io',
  'bo-customer-support-level-1@shares.io',
  'bo-fincrime-level-1@shares.io',
  'bo-moderator-level-1@shares.io',
  'bo-tech-level-1@shares.io',
  'bo-marketing-level-1@shares.io',
  'bo-finance-level-1@shares.io',
  'bo-management-level-1@shares.io',
  'bo-hr-level-1@shares.io',
  'bo-b2b-level-1@shares.io',
  'bo-b2b-level-2@shares.io',
  'bo-trust-safety-level-1@shares.io',
] as const;

export const ZodBackofficeGroupsLevel1T = z.enum(backofficeGroupsLevel1);

export const backofficeGroupsLevel2 = [
  'bo-compliance-level-2@shares.io',
  'bo-customer-support-level-2@shares.io',
  'bo-fincrime-level-2@shares.io',
  'bo-operations-level-2@shares.io',
] as const;

export const ZodBackofficeGroupsLevel2T = z.enum(backofficeGroupsLevel2);

export const backofficeGroupsLevel3 = [
  'bo-customer-support-level-3@shares.io',
  'bo-fincrime-level-3@shares.io',
] as const;

export const ZodBackofficeGroupsLevel3T = z.enum(backofficeGroupsLevel3);

export const backofficeGroupsAxa = [
  // Groups for tasks assignments
  'bo-axa-customer-service@external-shares.io',
  'bo-axa-customer-accounts-manager@external-shares.io',
  'bo-axa-compliance@external-shares.io',
  'bo-axa-aee@external-shares.io',
  'bo-axa-operation-and-delegation@external-shares.io',
  'bo-axa-marketing@external-shares.io',
  'bo-axa-quality@external-shares.io',
  // Groups for permissions
  'bo-axa-consultation@external-shares.io',
  'bo-axa-modification@external-shares.io',
  'bo-axa-operation@external-shares.io',
  'bo-axa-transmission@external-shares.io',
] as const;

export const ZodBackofficeGroupsAxaT = z.enum(backofficeGroupsAxa);

export type BackofficeGroupsLevel1T = z.infer<typeof ZodBackofficeGroupsLevel1T>;
export type BackofficeGroupsLevel2T = z.infer<typeof ZodBackofficeGroupsLevel2T>;
export type BackofficeGroupsLevel3T = z.infer<typeof ZodBackofficeGroupsLevel3T>;
export type BackofficeGroupsAxaT = z.infer<typeof ZodBackofficeGroupsAxaT>;

export const ZodBackofficeGroupsT = z.enum([
  adminGroupEmail,
  externalAdminGroupEmail,
  readOnlyAdminGroupEmail,
  ...ZodBackofficeGroupsLevel1T.options,
  ...ZodBackofficeGroupsLevel2T.options,
  ...ZodBackofficeGroupsLevel3T.options,
  ...ZodBackofficeGroupsAxaT.options,
]);

export type BackofficeGroupsT = z.infer<typeof ZodBackofficeGroupsT>;

export const TasksAssignableBoGroupsEmails: BackofficeGroupsT[] = [
  'bo-fincrime-level-1@shares.io',
  'bo-fincrime-level-2@shares.io',
  'bo-fincrime-level-3@shares.io',
  'bo-customer-support-level-1@shares.io',
  'bo-customer-support-level-2@shares.io',
  'bo-customer-support-level-3@shares.io',
  'bo-moderator-level-1@shares.io',
  'bo-compliance-level-1@shares.io',
  'bo-compliance-level-2@shares.io',
  'bo-product-level-1@shares.io',
  'bo-operations-level-1@shares.io',
  'bo-operations-level-2@shares.io',
  'bo-product-level-1@shares.io',
  adminGroupEmail,
  externalAdminGroupEmail,
  'bo-axa-customer-service@external-shares.io',
  'bo-axa-customer-accounts-manager@external-shares.io',
  'bo-axa-compliance@external-shares.io',
  'bo-axa-aee@external-shares.io',
  'bo-axa-operation-and-delegation@external-shares.io',
  'bo-axa-marketing@external-shares.io',
  'bo-axa-quality@external-shares.io',
];

export type TasksAssignableBoGroupsEmailsT = (typeof TasksAssignableBoGroupsEmails)[number];

const RolesLevel1Values = [
  'Product Level 1',
  'Operations Level 1',
  'Compliance Level 1',
  'Customer Support Level 1',
  'Moderator Level 1',
  'FinCrime Level 1',
  'Tech Level 1',
  'Marketing Level 1',
  'Finance Level 1',
  'Management Level 1',
  'HR Level 1',
  'B2B Level 1',
  'Trust Safety Level 1',
  'B2B External Customer Support Level 1',
  'B2B External Compliance Level 1',
] as const;
export const ZodRolesLevel1 = z.enum(RolesLevel1Values);
export type RolesLevel1T = z.TypeOf<typeof ZodRolesLevel1>;

const RolesLevel2Values = [
  'Customer Support Level 2',
  'FinCrime Level 2',
  'Compliance Level 2',
  'Operations Level 2',
  'B2B Level 2',
] as const;
export const ZodRolesLevel2 = z.enum(RolesLevel2Values);
export type RolesLevel2T = z.TypeOf<typeof ZodRolesLevel2>;

const RolesLevel3Values = ['Customer Support Level 3', 'FinCrime Level 3'] as const;
export const ZodRolesLevel3 = z.enum(RolesLevel3Values);
export type RolesLevel3T = z.TypeOf<typeof ZodRolesLevel3>;

const RolesAxaValues = [
  'AXA Marketing',
  'AXA Consultation',
  'AXA Modification',
  'AXA Operation',
  'AXA Transmission',
] as const;
export const ZodRolesAxa = z.enum(RolesAxaValues);
export type RolesAxaT = z.TypeOf<typeof ZodRolesAxa>;

const RolesValues = [
  'Default',
  adminGroupName,
  readOnlyAdminGroupName,
  ...RolesLevel1Values,
  ...RolesLevel2Values,
  ...RolesLevel3Values,
  ...RolesAxaValues,
] as const;
export const ZodRole = z.enum(RolesValues);
export type RoleT = z.TypeOf<typeof ZodRole>;

const BOResourceValues = [
  'authorization/permissions_roles',
  'authorization/role_definitions',
  'backend_version',
  'edit_requests',
  'tasks/ml_alert',
  'tasks/moderation',
  'tasks',
  'tasks/assign',
  'tasks/uniqueness/approve',
  'tasks/uniqueness/reject',
  'tasks/await_customer',
  'tasks/pending',
  'tasks/self_assign',
  'tasks/poa/retry',
  'tasks/poa/approve',
  'tasks/poa/reject',
  'tasks/sof/retry',
  'tasks/sof/approve',
  'tasks/sof/reject',
  'tasks/screening/approve',
  'tasks/screening/approve_high_risk',
  'tasks/screening/approve_pep',
  'tasks/screening/reject',
  'tasks/flagged_activity/delete',
  'tasks/flagged_activity/keep',
  'tasks/flagged_user/resolve',
  'tasks/transaction_reporting_id/approve',
  'tasks/transaction_reporting_id/reject',
  'tasks/periodic_review/approve',
  'tasks/periodic_review/reject',
  'tasks/risk_score_updated/approve',
  'tasks/risk_score_updated/reject',
  'tasks/risk_score_updated',
  'tasks/assign_bo_user',
  'tasks/screening_monitoring/approve',
  'tasks/screening_monitoring/approve_pep',
  'tasks/screening_monitoring/reject',
  'tasks/screening_monitoring_v2/approve',
  'tasks/screening_monitoring_v2/reject',
  'tasks/mdlr_bank_name_failed/approve',
  'tasks/mdlr_bank_name_failed/reject',
  'tasks/mdlr_bank_name_failed/retry',
  'tasks/unmatched_payment/approve',
  'tasks/unmatched_payment/approve_dry_run',
  'tasks/unmatched_payment/reject',
  'tasks/archive_task',
  'tasks/manual_proof_address/approve',
  'tasks/manual_proof_address/reject',
  'tasks/manual_proof_address/retry',
  'tasks/business_proof_identity/approve',
  'tasks/business_proof_identity/reject',
  'tasks/business_proof_identity/retry',
  'tasks/business_screening/approve',
  'tasks/business_screening/deny',
  'tasks/proof_identity/approve',
  'tasks/proof_identity/reject',
  'tasks/proof_identity/retry',
  'tasks/b2b_payment_compliance_review/approve',
  'tasks/b2b_payment_compliance_review/reject',
  'tasks/b2b_withdrawal/approve',
  'tasks/b2b_withdrawal/reject',
  'tasks/b2b_withdrawal/retry',
  'tasks/b2b_transfer_request/approve',
  'tasks/b2b_transfer_request/reject',
  'tasks/b2c_transfer_request/approve',
  'tasks/b2c_transfer_request/reject',
  'tasks/manual_source_of_funds/approve',
  'tasks/manual_source_of_funds/reject',
  'tasks/manual_source_of_funds/retry',
  'tasks/manual_proof_identity_v2/approve',
  'tasks/manual_proof_identity_v2/reject',
  'tasks/manual_proof_identity_v2/retry',
  'tasks/address_change_review/approve',
  'tasks/address_change_review/reject',
  'tasks/address_change_review/retry',
  'tasks/wm_proof_identity/approve',
  'tasks/wm_proof_identity/reject',
  'tasks/wm_proof_identity/retry',
  'tasks/wm_business_proof_identity/approve',
  'tasks/wm_business_proof_identity/reject',
  'tasks/wm_business_proof_identity/retry',
  'tasks/b2b_source_of_funds/approve',
  'tasks/b2b_source_of_funds/reject',
  'tasks/b2b_source_of_funds/retry',
  'tasks/b2b_proof_identity/approve',
  'tasks/b2b_proof_identity/reject',
  'tasks/b2b_proof_identity/retry',
  'tasks/pea_transfer/approve',
  'tasks/pea_transfer/reject',
  'tasks/b2b_procedure',
  'tasks/b2b_procedure/civil_enforcement/approve',
  'tasks/b2b_procedure/decease/approve',
  'tasks/b2b_eckert/approve',
  'tasks/b2b_correction/approve',
  'tasks/b2b_correction/reject',
  'tasks/b2b_screening_monitoring/approve',
  'instruments',
  'instruments/scheduled_edits',
  'instruments/scheduled_edits/cancel',
  'instruments/documents',
  'instruments/countries_status',
  'instruments/cutoff_dates',
  'instruments/cutoff_dates/bulk_update',
  'instruments/universe/acknowledge',
  'orders/stocks',
  'orders/cryptos',
  'orders/triggered',
  'orders/mutual_funds',
  'orders/mutual_funds/cancel',
  'transfers',
  'transfers/cancel',
  'transfers/retry',
  'transfers/retries',
  'transfers/attempts',
  'transfers/summary',
  'users/documents',
  'users',
  'users/tenants',
  'users/email_block',
  'users/balance',
  'users/balance/pea',
  'users/portfolio',
  'users/refresh_onboarding',
  'users/support/conversations',
  'users/vulnerable_comment',
  'users/report_summary',
  'users/friends',
  'users/followers',
  'users/all_documents',
  'users/dividends',
  'users/logins',
  'users/company',
  'users/company_refresh_lei',
  'users/wm_custody_fees',
  'users/investment_accounts',
  'users/investment_accounts/details',
  'users/investment_accounts/generate_proof_of_account',
  'users/investment_accounts/transfer_details',
  'users/investment_accounts/close',
  'settlements',
  'settlements/summary',
  'settlements/plan',
  'settlements/execute',
  'settlements/remove_orders',
  'collections',
  'collections/publish',
  'countries',
  'bo_users',
  'bo_users/bulk_toggle_confidential',
  'bo_users/bulk_toggle_closed_employees',
  'bo_users/readonly_api_key',
  'bo_comments',
  'logs',
  'cash_granting_requests',
  'user_action_requests',
  'review_requests',
  'review_requests/cancel',
  'review_requests/unlink_payment',
  'review_requests/self_assign',
  'review_requests/pea_transfer_out',
  'review_requests/cancel_aggregated_od_order',
  'review_requests/create_beneficiary',
  'b2b/review_requests/create_beneficiary',
  'b2b/review_requests/create_beneficiary/external_custodian_account',
  'b2b/review_requests/create_aggregated_order',
  'b2b/review_requests/trigger_collective_arbitrage',
  'b2b/review_requests/trigger_collective_arbitrage/buy_instruments',
  'b2b/review_requests/trigger_collective_arbitrage/employees',
  'b2b/review_requests/trigger_collective_arbitrage/sell_instruments',
  'b2b/review_requests/plan_to_plan_arbitrage',
  'b2b/review_requests/plan_to_plan_arbitrage/dry_run',
  'b2b/review_requests/plan_to_plan_arbitrage/employees',
  'b2b/review_requests/plan_to_plan_arbitrage/source_instruments',
  'b2b/review_requests/transfer_breton',
  'b2b/review_requests/transfer_breton/plans',
  'b2b/review_requests/transfer_breton/employees',
  'b2b/review_requests/transfer_breton/source_instruments',
  'b2b/review_requests/transfer_breton/destination_instruments',
  'b2b/review_requests/arbitrage',
  'b2b/review_requests/campaign_retractation',
  'b2b/review_requests/manual_lettering',
  'b2b/review_requests/manual_lettering/dry_run',
  'b2b/review_requests/prefund_withdrawal',
  'b2b/review_requests/withdrawal_approval',
  'review_requests/instant_oddo_submission',
  'review_requests/sell_instrument',
  'review_requests/sell_instrument/wallets',
  'review_requests/change_wealth_manager',
  'review_requests/change_wealth_manager/fees_preview',
  'logs/instrument',
  'logs/task',
  'logs/list',
  'logs/user',
  'logs/corporate_action',
  'logs/transfer',
  'logs/marketing_modal',
  'logs/b2b_company',
  'logs/b2b_employee',
  'logs/b2b_contract',
  'logs/info_banner',
  'logs/b2b_info_banner',
  'logs/savings_funds_request',
  'logs/b2b_campaign',
  'logs/b2b_contract_fee_charge',
  'logs/od_order',
  'logs/b2b_admin',
  'logs/marketing_market_update',
  'logs/wm_company',
  'logs/pea_transfer',
  'logs/co_payment',
  'logs/b2b_recurring_investment',
  'logs/banned_device',
  'logs/investment_plan',
  'logs/investment_account',
  'logs/backoffice_uploaded_file',
  'logs/bo_user',
  'logs/b2b_individual_operation',
  'logs/co_payment_refund',
  'logs/b2b_investor_profile',
  'reported_contents/users',
  'reported_contents/users/activity',
  'marketing_modals',
  'marketing_modals/publish',
  'marketing/market_updates',
  'marketing/market_updates/action',
  'beneficiaries',
  'async_flows',
  'async_flows/refresh',
  'async_flows/archive',
  'sepa_mandates',
  'shares_corporate_actions',
  'shares_corporate_actions/by_payable_date',
  'shares_corporate_actions/announcements',
  'shares_corporate_actions/announcements/review',
  'shares_corporate_actions/impacted_users/omnibus',
  'shares_corporate_actions/data_sources',
  'shares_corporate_actions/related_ca_nta/omnibus',
  'shares_corporate_actions/related_ca_nta/omnibus/unmatched',
  'shares_corporate_actions/allocations/omnibus',
  'shares_corporate_actions/allocations',
  'shares_corporate_actions/flow_status',
  'shares_corporate_actions/add_ca_event',
  'withdrawal_settlements',
  'cards',
  'sqs',
  'sqs/replay',
  'b2b/companies',
  'b2b/companies/search',
  'b2b/companies/search/directors',
  'b2b/companies/plans',
  'b2b/companies/plans/matching_contribution_config',
  'b2b/companies/plans/search',
  'b2b/companies/dashboard/active_campaigns',
  'b2b/companies/dashboard/savings',
  'b2b/companies/dashboard/matching_contributions',
  'b2b/companies/dashboard/affiliates',
  'b2b/companies/documents',
  'b2b/companies/documents_er',
  'b2b/companies/payment_methods',
  'b2b/companies/payment_methods/bank_details',
  'b2b/companies/payment_methods/draft_unsigned_mandate',
  'b2b/companies/instruments',
  'b2b/companies/restrictions',
  'b2b/companies/balance',
  'b2b/companies/edit_requests',
  'b2b/companies/auto_approved_edit_requests',
  'b2b/companies/edit_requests/action',
  'b2b/companies/withdrawals',
  'b2b/companies/treasury',
  'b2b/companies/treasury/link',
  'b2b/companies/treasury/candidate_payments',
  'b2b/investor_profile',
  'b2b/employees',
  'b2b/employees/last_otp',
  'b2b/employees/authentication',
  'b2b/employees/activation_code',
  'b2b/employees/login_link',
  'b2b/employees/authorize_login_link',
  'b2b/employees/edit_requests',
  'b2b/employees/edit_requests/action',
  'b2b/employees/er_eligibility',
  'b2b/employees/auto_approved_edit_requests',
  'b2b/employees/bulk_create',
  'b2b/employees/bulk_create_validate',
  'b2b/employees/bulk_create_multi_company',
  'b2b/employees/bulk_create_multi_company_validate',
  'b2b/employees/bulk_upload/results',
  'b2b/employees/bulk_upload/errors',
  'b2b/employees/send_activated_invitation',
  'b2b/employees/transfer_refund',
  'b2b/employees/bulk_activated_invitation',
  'b2b/employees/restrictions',
  'b2b/employees/balance',
  'b2b/employees/held_instruments',
  'b2b/employees/taiga_plans',
  'b2b/employees/available_positions',
  'b2b/employees/deposit_bank_info',
  'b2b/employees/portfolio',
  'b2b/employees/matching_contributions',
  'b2b/employees/documents',
  'b2b/employees/locked_funds',
  'b2b/employees/generate_account_statement',
  'b2b/admins',
  'b2b/admins/login_link',
  'b2b/admins/restricted_modification',
  'b2b/admins/authorize_login_link',
  'b2b/admins/last_otp',
  'b2b/admins/request_otp',
  'b2b/admins/invite',
  'b2b/orders',
  'b2b/orders/cancel',
  'b2b/orders/fully_managed_plan_contributions',
  'b2b/aggregated_orders',
  'b2b/internal_account_withdrawals',
  'b2b/internal_account_withdrawals/review',
  'b2b/withdrawals',
  'b2b/withdrawals/amounts',
  'b2b/withdrawals/eligible_allocations',
  'b2b/withdrawals/bulk',
  'b2b/withdrawals/bulk/template',
  'b2b/withdrawals/bulk/positions',
  'b2b/withdrawals/bulk/payout_information',
  'b2b/withdrawals/cancel',
  'b2b/savings_funds_requests',
  'b2b/savings_funds_requests/retry_sepa_dd',
  'b2b/savings_funds_requests/cancel',
  'b2b/savings_funds_requests/refresh',
  'b2b/savings_funds_requests/regenerate_document',
  'b2b/savings_funds_requests/employees',
  'b2b/campaigns',
  'b2b/campaigns/migrated',
  'b2b/campaigns/payouts',
  'b2b/campaigns/dashboard/answer_rate',
  'b2b/campaigns/dashboard/amounts',
  'b2b/campaigns/allocations',
  'b2b/campaigns/plan_breakdown',
  'b2b/campaigns/employees_awaiting_response',
  'b2b/campaigns/calculate_matching_contributions',
  'b2b/campaigns/detailed_reporting',
  'b2b/campaigns/refresh',
  'b2b/recurring_fee_charges',
  'b2b/recurring_investments',
  'b2b/recurring_investments/cancel',
  'b2b/recurring_investments/executions',
  'b2b/recurring_investments/allocations',
  'b2b/review_requests',
  'b2b/review_requests/cancel',
  'b2b/review_requests/self_assign',
  'b2b/status_files',
  'b2b/contracts',
  'b2b/contracts/edit',
  'b2b/contracts/documents',
  'b2b/contracts/distributor/login_link',
  'b2b/distributors',
  'b2b/countries_blacklist',
  'b2b/arbitrage_restrictions_instruments',
  'b2b/arbitrage_restrictions_plans',
  'b2b/contract_fee_charges',
  'b2b/transfer_requests',
  'b2b/transfer_requests/fre211',
  'b2b/transfer_requests/origin_fre211',
  'b2b/transfer_requests/fund_mapping',
  'b2b/transfer_requests/destination_fre211',
  'b2b/transfer_requests/destination_fre211_migration',
  'b2b/transfer_requests/resume_flow',
  'b2b/info_banner',
  'b2b/info_banner/publish',
  'b2b/services',
  'b2b/operations_er',
  'b2b/individual_operations',
  'b2b/individual_operations/orders',
  'b2b/individual_operations/summary',
  'b2b/individual_operations/cancel_voluntary_deposit',
  'b2b/recurring_investments_import',
  'b2b/recurring_investments_import/validate',
  'b2b/recurring_investments_import/execute',
  'b2b/accounting/reports',
  'b2b/accounting/reports_v2',
  'b2b/accounting/unlettered_operations',
  'info_banner',
  'info_banner/publish',
  'activity_posts',
  'activity_posts/delete',
  'activity_posts/review',
  'ops_actions/refresh_kid_article',
  'ops_actions/treasury_movements',
  'ops_actions/update_b2b_contract_relationship_type',
  'ops_actions/merge_mdlr_customer',
  'ops_actions/update_eltif_user_ledger',
  'ops_actions/change_order_instrument',
  'ops_actions/indexes',
  'ops_actions/email_whitelist',
  'ops_actions/banned_devices',
  'ops_actions/shares_pro_transfers',
  'b2b/ops_actions/merge_users',
  'b2b/ops_actions/merge_users/preview',
  'b2b/ops_actions/update_passive_nav',
  'b2b/ops_actions/update_passive_nav/preview',
  'b2b/ops_actions/merge_employees',
  'b2b/ops_actions/merge_employees/preview',
  'b2b/ops_actions/export_pmpa',
  'b2b/ops_actions/force_cancel_campaign',
  'b2b/ops_actions/corrections',
  'b2b/ops_actions/corrections/orders',
  'b2b/ops_actions/migrate_er_data',
  'b2b/ops_actions/refund_companies',
  'b2b/ops_actions/multi_company_bulk_upload',
  'b2b/shares_corporate_actions/send_letter',
  'b2b/ops_actions/generate_rvp_document',
  'b2b/ops_actions/send_oddo_document_manually',
  'qa/oddo/company_deposit',
  'investment_plans',
  'investment_plans/allocations',
  'investment_plans/executions',
  'investment_plans/executions/cancel',
  'investment_plans/executions/refresh',
  'investment_plans/updates',
  'investment_plans/portfolio',
  'client_approval_links',
  'withdrawals',
  'withdrawals/review',
  'presigned_url',
  'bo_uploaded_files',
  'bo_uploaded_files/confirm',
  'fourthline_workflows',
  'fourthline_workflows/download_report',
  'wm/companies',
  'wm/companies/documents',
  'wm/companies/fees',
  'wm/companies/sso_providers',
  'wm/companies/tenants',
  'wm/companies/universes',
  'wm/companies/universes/reset',
  'wm/admins',
  'wm/admins/login_link',
  'wm/admins/authorize_login_link',
  'wm/universes',
  'wm/universes/assets_csv',
  'wm/orders/eltif/force_cancel',
  'wm/orders/structured_product/force_cancel',
  'alpaca_account_withdrawals',
  'alpaca_account_withdrawals/review',
  'b2b/notifications',
  'b2b/notifications/isins',
  'b2b/notifications/archive',
  'checkout_chargebacks',
  'b2b/notifications/documents',
  'status/ping',
  'formance',
] as const;

export const ZodPermissionResource = z.enum(BOResourceValues);
export const ZodPermissionResourceWithWildcard = z.enum([
  ...ZodPermissionResource.options,
  '*',
] as const);
export type PermissionResourceT = z.TypeOf<typeof ZodPermissionResource>;
export type PermissionResourceTWithWildcard = z.TypeOf<typeof ZodPermissionResourceWithWildcard>;

export const BOResourcesAxaEnv = {
  'authorization/permissions_roles': true,
  'authorization/role_definitions': true,
  backend_version: true,
  edit_requests: true,
  'tasks/ml_alert': true,
  'tasks/moderation': false,
  tasks: true,
  'tasks/assign': true,
  'tasks/uniqueness/approve': true,
  'tasks/uniqueness/reject': true,
  'tasks/await_customer': true,
  'tasks/pending': true,
  'tasks/self_assign': true,
  'tasks/poa/retry': true,
  'tasks/poa/approve': true,
  'tasks/poa/reject': true,
  'tasks/sof/retry': false,
  'tasks/sof/approve': false,
  'tasks/sof/reject': false,
  'tasks/screening/approve': false,
  'tasks/screening/approve_high_risk': false,
  'tasks/screening/approve_pep': false,
  'tasks/screening/reject': false,
  'tasks/flagged_activity/delete': false,
  'tasks/flagged_activity/keep': false,
  'tasks/flagged_user/resolve': false,
  'tasks/transaction_reporting_id/approve': true,
  'tasks/transaction_reporting_id/reject': true,
  'tasks/periodic_review/approve': false,
  'tasks/periodic_review/reject': false,
  'tasks/risk_score_updated/approve': false,
  'tasks/risk_score_updated/reject': false,
  'tasks/risk_score_updated': false,
  'tasks/assign_bo_user': true,
  'tasks/screening_monitoring/approve': false,
  'tasks/screening_monitoring/approve_pep': false,
  'tasks/screening_monitoring/reject': false,
  'tasks/screening_monitoring_v2/approve': false,
  'tasks/screening_monitoring_v2/reject': false,
  'tasks/mdlr_bank_name_failed/approve': true,
  'tasks/mdlr_bank_name_failed/reject': true,
  'tasks/mdlr_bank_name_failed/retry': true,
  'tasks/unmatched_payment/approve': true,
  'tasks/unmatched_payment/approve_dry_run': true,
  'tasks/unmatched_payment/reject': true,
  'tasks/archive_task': true,
  'tasks/manual_proof_address/approve': false,
  'tasks/manual_proof_address/reject': false,
  'tasks/manual_proof_address/retry': false,
  'tasks/business_proof_identity/approve': false,
  'tasks/business_proof_identity/reject': false,
  'tasks/business_proof_identity/retry': false,
  'tasks/business_screening/approve': false,
  'tasks/business_screening/deny': false,
  'tasks/proof_identity/approve': false,
  'tasks/proof_identity/reject': false,
  'tasks/proof_identity/retry': false,
  'tasks/b2b_payment_compliance_review/approve': true,
  'tasks/b2b_payment_compliance_review/reject': true,
  'tasks/b2b_withdrawal/approve': true,
  'tasks/b2b_withdrawal/reject': true,
  'tasks/b2b_withdrawal/retry': true,
  'tasks/b2b_transfer_request/approve': true,
  'tasks/b2b_transfer_request/reject': true,
  'tasks/b2c_transfer_request/approve': false,
  'tasks/b2c_transfer_request/reject': false,
  'tasks/manual_source_of_funds/approve': false,
  'tasks/manual_source_of_funds/reject': false,
  'tasks/manual_source_of_funds/retry': false,
  'tasks/manual_proof_identity_v2/approve': false,
  'tasks/manual_proof_identity_v2/reject': false,
  'tasks/manual_proof_identity_v2/retry': false,
  'tasks/address_change_review/approve': true,
  'tasks/address_change_review/reject': true,
  'tasks/address_change_review/retry': true,
  'tasks/wm_proof_identity/approve': false,
  'tasks/wm_proof_identity/reject': false,
  'tasks/wm_proof_identity/retry': false,
  'tasks/wm_business_proof_identity/approve': false,
  'tasks/wm_business_proof_identity/reject': false,
  'tasks/wm_business_proof_identity/retry': false,
  'tasks/b2b_source_of_funds/approve': true,
  'tasks/b2b_source_of_funds/reject': true,
  'tasks/b2b_source_of_funds/retry': true,
  'tasks/b2b_proof_identity/approve': true,
  'tasks/b2b_proof_identity/reject': true,
  'tasks/b2b_proof_identity/retry': true,
  'tasks/pea_transfer/approve': false,
  'tasks/pea_transfer/reject': false,
  'tasks/b2b_procedure': true,
  'tasks/b2b_procedure/civil_enforcement/approve': true,
  'tasks/b2b_procedure/decease/approve': true,
  'tasks/b2b_eckert/approve': true,
  'tasks/b2b_correction/approve': true,
  'tasks/b2b_correction/reject': true,
  'tasks/b2b_screening_monitoring/approve': true,
  instruments: true,
  'instruments/scheduled_edits': true,
  'instruments/scheduled_edits/cancel': true,
  'instruments/documents': true,
  'instruments/countries_status': true,
  'instruments/cutoff_dates': true,
  'instruments/cutoff_dates/bulk_update': true,
  'instruments/universe/acknowledge': true,
  'orders/stocks': false,
  'orders/cryptos': false,
  'orders/triggered': true,
  'orders/mutual_funds': false,
  'orders/mutual_funds/cancel': false,
  transfers: true,
  'transfers/cancel': true,
  'transfers/retry': true,
  'transfers/retries': true,
  'transfers/attempts': true,
  'transfers/summary': true,
  'users/documents': true,
  users: true,
  'users/tenants': true,
  'users/email_block': true,
  'users/balance': false,
  'users/balance/pea': false,
  'users/portfolio': false,
  'users/refresh_onboarding': true,
  'users/support/conversations': true,
  'users/vulnerable_comment': true,
  'users/report_summary': true,
  'users/friends': false,
  'users/followers': false,
  'users/all_documents': true,
  'users/dividends': false,
  'users/logins': true,
  'users/company': false,
  'users/wm_custody_fees': false,
  'users/company_refresh_lei': false,
  'users/investment_accounts': true,
  'users/investment_accounts/details': true,
  'users/investment_accounts/generate_proof_of_account': true,
  'users/investment_accounts/transfer_details': true,
  'users/investment_accounts/close': true,
  settlements: true,
  'settlements/summary': true,
  'settlements/plan': true,
  'settlements/execute': true,
  'settlements/remove_orders': false,
  collections: false,
  'collections/publish': false,
  countries: true,
  bo_users: true,
  'bo_users/bulk_toggle_confidential': true,
  'bo_users/bulk_toggle_closed_employees': true,
  'bo_users/readonly_api_key': true,
  bo_comments: true,
  logs: true,
  cash_granting_requests: false,
  user_action_requests: true,
  review_requests: true,
  'review_requests/cancel': true,
  'review_requests/unlink_payment': true,
  'review_requests/self_assign': true,
  'review_requests/pea_transfer_out': false,
  'review_requests/cancel_aggregated_od_order': false,
  'review_requests/create_beneficiary': true,
  'review_requests/instant_oddo_submission': false,
  'review_requests/sell_instrument': false,
  'review_requests/sell_instrument/wallets': false,
  'review_requests/change_wealth_manager': false,
  'review_requests/change_wealth_manager/fees_preview': false,
  'b2b/review_requests/create_beneficiary': true,
  'b2b/review_requests/create_beneficiary/external_custodian_account': true,
  'b2b/review_requests/create_aggregated_order': true,
  'b2b/review_requests/trigger_collective_arbitrage': true,
  'b2b/review_requests/trigger_collective_arbitrage/buy_instruments': true,
  'b2b/review_requests/trigger_collective_arbitrage/employees': true,
  'b2b/review_requests/trigger_collective_arbitrage/sell_instruments': true,
  'b2b/review_requests/plan_to_plan_arbitrage': true,
  'b2b/review_requests/plan_to_plan_arbitrage/dry_run': true,
  'b2b/review_requests/plan_to_plan_arbitrage/employees': true,
  'b2b/review_requests/plan_to_plan_arbitrage/source_instruments': true,
  'b2b/review_requests/transfer_breton': true,
  'b2b/review_requests/transfer_breton/plans': true,
  'b2b/review_requests/transfer_breton/employees': true,
  'b2b/review_requests/transfer_breton/source_instruments': true,
  'b2b/review_requests/transfer_breton/destination_instruments': true,
  'b2b/review_requests/arbitrage': true,
  'b2b/review_requests/campaign_retractation': true,
  'b2b/review_requests/manual_lettering': true,
  'b2b/review_requests/manual_lettering/dry_run': true,
  'b2b/review_requests/prefund_withdrawal': true,
  'b2b/review_requests/withdrawal_approval': true,
  'logs/instrument': true,
  'logs/task': true,
  'logs/list': false,
  'logs/user': true,
  'logs/corporate_action': true,
  'logs/transfer': true,
  'logs/marketing_modal': false,
  'logs/b2b_company': true,
  'logs/b2b_employee': true,
  'logs/b2b_contract': true,
  'logs/info_banner': false,
  'logs/b2b_info_banner': true,
  'logs/savings_funds_request': true,
  'logs/b2b_campaign': true,
  'logs/b2b_contract_fee_charge': true,
  'logs/od_order': true,
  'logs/b2b_admin': true,
  'logs/marketing_market_update': false,
  'logs/wm_company': false,
  'logs/pea_transfer': true,
  'logs/co_payment': true,
  'logs/b2b_recurring_investment': true,
  'logs/banned_device': true,
  'logs/investment_plan': false,
  'logs/investment_account': false,
  'logs/backoffice_uploaded_file': true,
  'logs/bo_user': true,
  'logs/b2b_individual_operation': true,
  'logs/co_payment_refund': true,
  'logs/b2b_investor_profile': true,
  'reported_contents/users': false,
  'reported_contents/users/activity': false,
  marketing_modals: false,
  'marketing_modals/publish': false,
  'marketing/market_updates': false,
  'marketing/market_updates/action': false,
  beneficiaries: true,
  async_flows: true,
  'async_flows/refresh': true,
  'async_flows/archive': true,
  sepa_mandates: true,
  shares_corporate_actions: true,
  'shares_corporate_actions/by_payable_date': true,
  'shares_corporate_actions/announcements': true,
  'shares_corporate_actions/announcements/review': true,
  'shares_corporate_actions/impacted_users/omnibus': true,
  'shares_corporate_actions/data_sources': true,
  'shares_corporate_actions/related_ca_nta/omnibus': true,
  'shares_corporate_actions/related_ca_nta/omnibus/unmatched': true,
  'shares_corporate_actions/allocations/omnibus': true,
  'shares_corporate_actions/allocations': true,
  'shares_corporate_actions/flow_status': true,
  'shares_corporate_actions/add_ca_event': false,
  withdrawal_settlements: true,
  cards: true,
  sqs: true,
  'sqs/replay': true,
  'b2b/companies': true,
  'b2b/companies/search': true,
  'b2b/companies/search/directors': true,
  'b2b/companies/plans': true,
  'b2b/companies/plans/matching_contribution_config': true,
  'b2b/companies/plans/search': true,
  'b2b/companies/dashboard/active_campaigns': true,
  'b2b/companies/dashboard/savings': true,
  'b2b/companies/dashboard/matching_contributions': true,
  'b2b/companies/dashboard/affiliates': true,
  'b2b/companies/documents': true,
  'b2b/companies/documents_er': true,
  'b2b/companies/payment_methods': true,
  'b2b/companies/payment_methods/bank_details': true,
  'b2b/companies/payment_methods/draft_unsigned_mandate': true,
  'b2b/companies/instruments': true,
  'b2b/companies/restrictions': true,
  'b2b/companies/balance': true,
  'b2b/companies/edit_requests': true,
  'b2b/companies/auto_approved_edit_requests': true,
  'b2b/companies/edit_requests/action': true,
  'b2b/companies/withdrawals': true,
  'b2b/companies/treasury': true,
  'b2b/companies/treasury/link': true,
  'b2b/companies/treasury/candidate_payments': true,
  'b2b/investor_profile': true,
  'b2b/employees': true,
  'b2b/employees/last_otp': true,
  'b2b/employees/authentication': true,
  'b2b/employees/activation_code': true,
  'b2b/employees/login_link': true,
  'b2b/employees/authorize_login_link': true,
  'b2b/employees/edit_requests': true,
  'b2b/employees/edit_requests/action': true,
  'b2b/employees/er_eligibility': true,
  'b2b/employees/auto_approved_edit_requests': true,
  'b2b/employees/bulk_create': true,
  'b2b/employees/bulk_create_validate': true,
  'b2b/employees/bulk_create_multi_company': true,
  'b2b/employees/bulk_create_multi_company_validate': true,
  'b2b/employees/bulk_upload/results': true,
  'b2b/employees/bulk_upload/errors': true,
  'b2b/employees/send_activated_invitation': true,
  'b2b/employees/transfer_refund': true,
  'b2b/employees/bulk_activated_invitation': true,
  'b2b/employees/restrictions': true,
  'b2b/employees/balance': true,
  'b2b/employees/held_instruments': true,
  'b2b/employees/taiga_plans': true,
  'b2b/employees/available_positions': true,
  'b2b/employees/deposit_bank_info': true,
  'b2b/employees/portfolio': true,
  'b2b/employees/matching_contributions': true,
  'b2b/employees/documents': true,
  'b2b/employees/locked_funds': true,
  'b2b/employees/generate_account_statement': true,
  'b2b/admins': true,
  'b2b/admins/login_link': true,
  'b2b/admins/restricted_modification': true,
  'b2b/admins/authorize_login_link': true,
  'b2b/admins/request_otp': true,
  'b2b/admins/invite': true,
  'b2b/admins/last_otp': true,
  'b2b/orders': true,
  'b2b/orders/cancel': true,
  'b2b/orders/fully_managed_plan_contributions': true,
  'b2b/aggregated_orders': true,
  'b2b/internal_account_withdrawals': true,
  'b2b/internal_account_withdrawals/review': true,
  'b2b/withdrawals': true,
  'b2b/withdrawals/amounts': true,
  'b2b/withdrawals/eligible_allocations': true,
  'b2b/withdrawals/bulk': true,
  'b2b/withdrawals/bulk/template': true,
  'b2b/withdrawals/bulk/positions': true,
  'b2b/withdrawals/bulk/payout_information': true,
  'b2b/withdrawals/cancel': true,
  'b2b/savings_funds_requests': true,
  'b2b/savings_funds_requests/retry_sepa_dd': true,
  'b2b/savings_funds_requests/cancel': true,
  'b2b/savings_funds_requests/refresh': true,
  'b2b/savings_funds_requests/regenerate_document': true,
  'b2b/savings_funds_requests/employees': true,
  'b2b/campaigns': true,
  'b2b/campaigns/migrated': true,
  'b2b/campaigns/payouts': true,
  'b2b/campaigns/dashboard/answer_rate': true,
  'b2b/campaigns/dashboard/amounts': true,
  'b2b/campaigns/allocations': true,
  'b2b/campaigns/plan_breakdown': true,
  'b2b/campaigns/employees_awaiting_response': true,
  'b2b/campaigns/calculate_matching_contributions': true,
  'b2b/campaigns/detailed_reporting': true,
  'b2b/campaigns/refresh': true,
  'b2b/recurring_fee_charges': true,
  'b2b/recurring_investments': true,
  'b2b/recurring_investments/cancel': true,
  'b2b/recurring_investments/executions': true,
  'b2b/recurring_investments/allocations': true,
  'b2b/review_requests': true,
  'b2b/review_requests/cancel': true,
  'b2b/review_requests/self_assign': true,
  'b2b/status_files': true,
  'b2b/contracts': true,
  'b2b/contracts/edit': true,
  'b2b/contracts/documents': true,
  'b2b/contracts/distributor/login_link': true,
  'b2b/distributors': true,
  'b2b/countries_blacklist': true,
  'b2b/arbitrage_restrictions_instruments': true,
  'b2b/arbitrage_restrictions_plans': true,
  'b2b/contract_fee_charges': true,
  'b2b/transfer_requests': true,
  'b2b/transfer_requests/fre211': true,
  'b2b/transfer_requests/origin_fre211': true,
  'b2b/transfer_requests/fund_mapping': true,
  'b2b/transfer_requests/destination_fre211': true,
  'b2b/transfer_requests/destination_fre211_migration': true,
  'b2b/transfer_requests/resume_flow': true,
  'b2b/info_banner': true,
  'b2b/info_banner/publish': true,
  'b2b/services': true,
  'b2b/operations_er': true,
  'b2b/individual_operations': true,
  'b2b/individual_operations/orders': true,
  'b2b/individual_operations/summary': true,
  'b2b/individual_operations/cancel_voluntary_deposit': true,
  'b2b/recurring_investments_import': true,
  'b2b/recurring_investments_import/validate': true,
  'b2b/recurring_investments_import/execute': true,
  'b2b/accounting/reports': true,
  'b2b/accounting/reports_v2': true,
  'b2b/accounting/unlettered_operations': true,
  info_banner: false,
  'info_banner/publish': false,
  activity_posts: false,
  'activity_posts/delete': false,
  'activity_posts/review': false,
  'ops_actions/refresh_kid_article': true,
  'ops_actions/treasury_movements': true,
  'ops_actions/update_b2b_contract_relationship_type': true,
  'ops_actions/merge_mdlr_customer': true,
  'ops_actions/update_eltif_user_ledger': false,
  'ops_actions/change_order_instrument': true,
  'ops_actions/indexes': true,
  'ops_actions/email_whitelist': true,
  'ops_actions/banned_devices': true,
  'ops_actions/shares_pro_transfers': false,
  'b2b/ops_actions/merge_users': true,
  'b2b/ops_actions/merge_users/preview': true,
  'b2b/ops_actions/update_passive_nav': true,
  'b2b/ops_actions/update_passive_nav/preview': true,
  'b2b/ops_actions/merge_employees': true,
  'b2b/ops_actions/merge_employees/preview': true,
  'b2b/ops_actions/export_pmpa': true,
  'b2b/ops_actions/force_cancel_campaign': true,
  'b2b/ops_actions/corrections': true,
  'b2b/ops_actions/corrections/orders': true,
  'b2b/ops_actions/migrate_er_data': true,
  'b2b/ops_actions/refund_companies': true,
  'b2b/ops_actions/multi_company_bulk_upload': true,
  'b2b/shares_corporate_actions/send_letter': true,
  'b2b/ops_actions/generate_rvp_document': true,
  'b2b/ops_actions/send_oddo_document_manually': true,
  'qa/oddo/company_deposit': true,
  investment_plans: false,
  'investment_plans/allocations': false,
  'investment_plans/executions': false,
  'investment_plans/executions/cancel': false,
  'investment_plans/executions/refresh': false,
  'investment_plans/updates': false,
  'investment_plans/portfolio': false,
  client_approval_links: false,
  withdrawals: false,
  'withdrawals/review': false,
  presigned_url: true,
  bo_uploaded_files: true,
  'bo_uploaded_files/confirm': true,
  fourthline_workflows: true,
  'fourthline_workflows/download_report': true,
  'wm/companies': false,
  'wm/companies/documents': false,
  'wm/companies/fees': false,
  'wm/companies/sso_providers': false,
  'wm/companies/tenants': false,
  'wm/companies/universes': false,
  'wm/companies/universes/reset': false,
  'wm/admins': false,
  'wm/admins/login_link': false,
  'wm/admins/authorize_login_link': false,
  'wm/universes': false,
  'wm/universes/assets_csv': false,
  'wm/orders/eltif/force_cancel': false,
  'wm/orders/structured_product/force_cancel': false,
  alpaca_account_withdrawals: false,
  'alpaca_account_withdrawals/review': false,
  'b2b/notifications': true,
  'b2b/notifications/isins': true,
  'b2b/notifications/archive': true,
  checkout_chargebacks: true,
  'b2b/notifications/documents': true,
  'status/ping': false,
  formance: true,
} as const satisfies Record<PermissionResourceT, boolean>;

export type PermissionResourceAxaEnvT = {
  [K in keyof typeof BOResourcesAxaEnv]: (typeof BOResourcesAxaEnv)[K] extends true ? K : never;
}[keyof typeof BOResourcesAxaEnv];

// This is the list of resources EXEMPTED from the blacklist system to hide
// data from b2b_companies where is_confidential = true
// BEWARE: if a resource contains personal information,
// It SHOULD NOT BE in the whitelist, because it should be in blacklist scope.
// Failure to do so will result in data leak.
export const BOResourcesAxaEnvWhitelist = [
  'authorization/permissions_roles',
  'authorization/role_definitions',
  'backend_version',
  'edit_requests',
  'tasks',
  'tasks/ml_alert',
  'tasks/assign',
  'tasks/assign_bo_user',
  'tasks/pending',
  'tasks/await_customer',
  'tasks/self_assign',
  'tasks/archive_task',
  'tasks/uniqueness/approve',
  'tasks/uniqueness/reject',
  'tasks/poa/retry',
  'tasks/poa/approve',
  'tasks/poa/reject',
  'tasks/transaction_reporting_id/approve',
  'tasks/transaction_reporting_id/reject',
  'tasks/mdlr_bank_name_failed/approve',
  'tasks/mdlr_bank_name_failed/reject',
  'tasks/mdlr_bank_name_failed/retry',
  'tasks/unmatched_payment/approve',
  'tasks/unmatched_payment/approve_dry_run',
  'tasks/unmatched_payment/reject',
  'tasks/address_change_review/approve',
  'tasks/address_change_review/reject',
  'tasks/address_change_review/retry',
  'tasks/b2b_payment_compliance_review/approve',
  'tasks/b2b_payment_compliance_review/reject',
  'tasks/b2b_withdrawal/approve',
  'tasks/b2b_withdrawal/reject',
  'tasks/b2b_withdrawal/retry',
  'tasks/b2b_transfer_request/approve',
  'tasks/b2b_transfer_request/reject',
  'tasks/b2b_source_of_funds/approve',
  'tasks/b2b_source_of_funds/reject',
  'tasks/b2b_source_of_funds/retry',
  'tasks/b2b_proof_identity/approve',
  'tasks/b2b_proof_identity/reject',
  'tasks/b2b_proof_identity/retry',
  'tasks/b2b_procedure',
  'tasks/b2b_procedure/civil_enforcement/approve',
  'tasks/b2b_procedure/decease/approve',
  'tasks/b2b_eckert/approve',
  'tasks/b2b_correction/approve',
  'tasks/b2b_correction/reject',
  'tasks/b2b_screening_monitoring/approve',
  'instruments',
  'instruments/scheduled_edits',
  'instruments/scheduled_edits/cancel',
  'instruments/documents',
  'instruments/countries_status',
  'instruments/cutoff_dates',
  'instruments/cutoff_dates/bulk_update',
  'instruments/universe/acknowledge',
  'users/documents',
  'users/tenants',
  'users/email_block',
  'users/refresh_onboarding',
  'users/support/conversations',
  'users/vulnerable_comment',
  'users/report_summary',
  'users/all_documents',
  'users/logins',
  'users/investment_accounts',
  'users/investment_accounts/details',
  'users/investment_accounts/generate_proof_of_account',
  'users/investment_accounts/transfer_details',
  'users/investment_accounts/close',
  'settlements',
  'settlements/summary',
  'settlements/plan',
  'settlements/execute',
  'countries',
  'bo_users',
  'bo_users/bulk_toggle_confidential',
  'bo_users/bulk_toggle_closed_employees',
  'bo_users/readonly_api_key',
  'bo_comments',
  'logs',
  'user_action_requests',
  'review_requests',
  'review_requests/cancel',
  'review_requests/unlink_payment',
  'review_requests/self_assign',
  'review_requests/create_beneficiary',
  'b2b/review_requests/create_beneficiary',
  'b2b/review_requests/create_beneficiary/external_custodian_account',
  'logs/instrument',
  'logs/task',
  'logs/user',
  'logs/corporate_action',
  'logs/transfer',
  'logs/b2b_company',
  'logs/b2b_employee',
  'logs/b2b_contract',
  'logs/b2b_info_banner',
  'logs/savings_funds_request',
  'logs/b2b_campaign',
  'logs/b2b_contract_fee_charge',
  'logs/od_order',
  'logs/b2b_admin',
  'logs/pea_transfer',
  'logs/co_payment',
  'logs/b2b_recurring_investment',
  'logs/banned_device',
  'logs/backoffice_uploaded_file',
  'logs/bo_user',
  'logs/b2b_individual_operation',
  'logs/co_payment_refund',
  'logs/b2b_investor_profile',
  'beneficiaries',
  'async_flows',
  'async_flows/refresh',
  'async_flows/archive',
  'sepa_mandates',
  'shares_corporate_actions',
  'shares_corporate_actions/by_payable_date',
  'shares_corporate_actions/announcements',
  'shares_corporate_actions/announcements/review',
  'shares_corporate_actions/impacted_users/omnibus',
  'shares_corporate_actions/data_sources',
  'shares_corporate_actions/related_ca_nta/omnibus',
  'shares_corporate_actions/related_ca_nta/omnibus/unmatched',
  'shares_corporate_actions/allocations/omnibus',
  'shares_corporate_actions/allocations',
  'shares_corporate_actions/flow_status',
  'withdrawal_settlements',
  'cards',
  'sqs',
  'sqs/replay',
  'ops_actions/refresh_kid_article',
  'ops_actions/treasury_movements',
  'ops_actions/update_b2b_contract_relationship_type',
  'ops_actions/merge_mdlr_customer',
  'ops_actions/change_order_instrument',
  'ops_actions/email_whitelist',
  'ops_actions/indexes',
  'ops_actions/banned_devices',
  'b2b/ops_actions/merge_users',
  'b2b/ops_actions/merge_users/preview',
  'b2b/ops_actions/update_passive_nav',
  'b2b/ops_actions/update_passive_nav/preview',
  'b2b/ops_actions/merge_employees',
  'b2b/ops_actions/merge_employees/preview',
  'b2b/ops_actions/export_pmpa',
  'b2b/ops_actions/force_cancel_campaign',
  'b2b/ops_actions/corrections',
  'b2b/ops_actions/corrections/orders',
  'b2b/ops_actions/migrate_er_data',
  'b2b/ops_actions/refund_companies',
  'b2b/ops_actions/multi_company_bulk_upload',
  'b2b/shares_corporate_actions/send_letter',
  'b2b/ops_actions/generate_rvp_document',
  'b2b/ops_actions/send_oddo_document_manually',
  'qa/oddo/company_deposit',
  'presigned_url',
  'bo_uploaded_files',
  'bo_uploaded_files/confirm',
  'fourthline_workflows',
  'fourthline_workflows/download_report',
  'transfers',
  'transfers/summary',
  'transfers/cancel',
  'transfers/retry',
  'transfers/retries',
  'transfers/attempts',
  'b2b/companies/search',
  'b2b/companies/search/directors',
  'b2b/companies/plans',
  'b2b/companies/plans/matching_contribution_config',
  'b2b/companies/plans/search',
  'b2b/companies/dashboard/active_campaigns',
  'b2b/companies/dashboard/savings',
  'b2b/companies/dashboard/matching_contributions',
  'b2b/companies/dashboard/affiliates',
  'b2b/companies/documents',
  'b2b/companies/documents_er',
  'b2b/companies/payment_methods',
  'b2b/companies/payment_methods/bank_details',
  'b2b/companies/payment_methods/draft_unsigned_mandate',
  'b2b/companies/instruments',
  'b2b/companies/restrictions',
  'b2b/companies/balance',
  'b2b/companies/edit_requests',
  'b2b/companies/auto_approved_edit_requests',
  'b2b/companies/edit_requests/action',
  'b2b/companies/withdrawals',
  'b2b/companies/treasury',
  'b2b/companies/treasury/link',
  'b2b/companies/treasury/candidate_payments',
  'b2b/employees/login_link',
  'b2b/employees/last_otp',
  'b2b/employees/activation_code',
  'b2b/employees/authorize_login_link',
  'b2b/employees/edit_requests',
  'b2b/employees/edit_requests/action',
  'b2b/employees/er_eligibility',
  'b2b/employees/auto_approved_edit_requests',
  'b2b/employees/bulk_create',
  'b2b/employees/bulk_create_validate',
  'b2b/employees/bulk_create_multi_company',
  'b2b/employees/bulk_create_multi_company_validate',
  'b2b/employees/bulk_upload/results',
  'b2b/employees/bulk_upload/errors',
  'b2b/employees/send_activated_invitation',
  'b2b/employees/transfer_refund',
  'b2b/employees/bulk_activated_invitation',
  'b2b/employees/restrictions',
  'b2b/employees/matching_contributions',
  'b2b/employees/deposit_bank_info',
  'b2b/employees/documents',
  'b2b/employees/locked_funds',
  'b2b/employees/generate_account_statement',
  'b2b/investor_profile',
  'b2b/admins',
  'b2b/admins/login_link',
  'b2b/admins/restricted_modification',
  'b2b/admins/authorize_login_link',
  'b2b/admins/request_otp',
  'b2b/admins/invite',
  'b2b/admins/last_otp',
  'b2b/orders/cancel',
  'b2b/orders/fully_managed_plan_contributions',
  'b2b/aggregated_orders',
  'b2b/withdrawals',
  'b2b/withdrawals/amounts',
  'b2b/withdrawals/eligible_allocations',
  'b2b/withdrawals/bulk',
  'b2b/withdrawals/bulk/template',
  'b2b/withdrawals/bulk/positions',
  'b2b/withdrawals/bulk/payout_information',
  'b2b/withdrawals/cancel',
  'b2b/internal_account_withdrawals',
  'b2b/internal_account_withdrawals/review',
  'b2b/savings_funds_requests',
  'b2b/savings_funds_requests/retry_sepa_dd',
  'b2b/savings_funds_requests/cancel',
  'b2b/savings_funds_requests/refresh',
  'b2b/savings_funds_requests/regenerate_document',
  'b2b/savings_funds_requests/employees',
  'b2b/campaigns/payouts',
  'b2b/campaigns/dashboard/answer_rate',
  'b2b/campaigns/dashboard/amounts',
  'b2b/campaigns/allocations',
  'b2b/campaigns/employees_awaiting_response',
  'b2b/campaigns/calculate_matching_contributions',
  'b2b/campaigns/detailed_reporting',
  'b2b/recurring_fee_charges',
  'b2b/recurring_investments',
  'b2b/review_requests',
  'b2b/review_requests/cancel',
  'b2b/review_requests/self_assign',
  'b2b/review_requests/create_aggregated_order',
  'b2b/review_requests/trigger_collective_arbitrage',
  'b2b/review_requests/trigger_collective_arbitrage/buy_instruments',
  'b2b/review_requests/trigger_collective_arbitrage/employees',
  'b2b/review_requests/trigger_collective_arbitrage/sell_instruments',
  'b2b/review_requests/plan_to_plan_arbitrage',
  'b2b/review_requests/plan_to_plan_arbitrage/dry_run',
  'b2b/review_requests/plan_to_plan_arbitrage/employees',
  'b2b/review_requests/plan_to_plan_arbitrage/source_instruments',
  'b2b/review_requests/transfer_breton',
  'b2b/review_requests/transfer_breton/plans',
  'b2b/review_requests/transfer_breton/employees',
  'b2b/review_requests/transfer_breton/source_instruments',
  'b2b/review_requests/transfer_breton/destination_instruments',
  'b2b/review_requests/arbitrage',
  'b2b/review_requests/campaign_retractation',
  'b2b/review_requests/manual_lettering',
  'b2b/review_requests/manual_lettering/dry_run',
  'b2b/review_requests/prefund_withdrawal',
  'b2b/review_requests/withdrawal_approval',
  'b2b/status_files',
  'b2b/contracts',
  'b2b/contracts/edit',
  'b2b/contracts/documents',
  'b2b/contracts/distributor/login_link',
  'b2b/distributors',
  'b2b/countries_blacklist',
  'b2b/arbitrage_restrictions_instruments',
  'b2b/arbitrage_restrictions_plans',
  'b2b/contract_fee_charges',
  'b2b/transfer_requests',
  'b2b/transfer_requests/fre211',
  'b2b/transfer_requests/origin_fre211',
  'b2b/transfer_requests/fund_mapping',
  'b2b/transfer_requests/destination_fre211',
  'b2b/transfer_requests/destination_fre211_migration',
  'b2b/transfer_requests/resume_flow',
  'b2b/info_banner',
  'b2b/info_banner/publish',
  'b2b/services',
  'b2b/operations_er',
  'b2b/campaigns/migrated',
  'b2b/individual_operations',
  'b2b/individual_operations/orders',
  'b2b/individual_operations/summary',
  'b2b/individual_operations/cancel_voluntary_deposit',
  'b2b/recurring_investments_import',
  'b2b/recurring_investments_import/validate',
  'b2b/recurring_investments_import/execute',
  'b2b/accounting/reports',
  'b2b/accounting/reports_v2',
  'b2b/accounting/unlettered_operations',
  'b2b/recurring_investments/cancel',
  'b2b/recurring_investments/executions',
  'b2b/recurring_investments/allocations',
  'b2b/notifications',
  'b2b/notifications/isins',
  'b2b/notifications/archive',
  'b2b/notifications/documents',
  'checkout_chargebacks',
  // Raw ledger data carries no company id to check confidentiality against; accepted for the
  // read-only Formance passthrough
  'formance',
] as const satisfies PermissionResourceAxaEnvT[];

export type PermissionResourceAxaEnvWhitelistT = (typeof BOResourcesAxaEnvWhitelist)[number];

export type PermissionResourceAxaEnvBlacklistT = Exclude<
  PermissionResourceAxaEnvT,
  PermissionResourceAxaEnvWhitelistT
>;

// TO FIX: later when we have a reference frame of implemented resources and actions, use that instead of this local type
export const BOResourcesAxaEnvWithCreateAction = [
  'b2b/employees',
] as const satisfies PermissionResourceAxaEnvBlacklistT[];
export type BOResourcesAxaEnvWithCreateActionT = (typeof BOResourcesAxaEnvWithCreateAction)[number];

export const ZodPermissionActionT = z.enum(['list', 'show', 'edit', 'create', 'delete']);
export const ZodPermissionActionTWithWildcard = z.enum([
  ...ZodPermissionActionT.options,
  '*',
] as const);

export type PermissionActionT = z.TypeOf<typeof ZodPermissionActionT>;
export type PermissionActionTWithWildcard = z.TypeOf<typeof ZodPermissionActionTWithWildcard>;

// NOTE: please do not include '*' in PermissionActionT and PermissionResourceT type enums
// because we use those schemas to parse incoming requests in middleware and * are not possible values
// '*' is however valid in configuration files
export type PermissionT = {
  action: PermissionActionTWithWildcard | PermissionActionTWithWildcard[];
  resource: PermissionResourceTWithWildcard;
};
export type PermissionAxaEnvT = {
  action: PermissionActionTWithWildcard | PermissionActionTWithWildcard[];
  resource: PermissionResourceAxaEnvT | '*';
};

// This is for incoming request analysis, we extract resource and action from request, no wildcard possible
export type PermissionRequestT = {
  action: PermissionActionT;
  resource: PermissionResourceT;
};

export type PermissionsByRoleLevel1T = Record<RolesLevel1T, PermissionT[]>;
export type PermissionsByRoleLevel2T = Record<RolesLevel2T, PermissionT[]>;
export type PermissionsByRoleLevel3T = Record<RolesLevel3T, PermissionT[]>;
export type PermissionByRoleAxaT = Record<RolesAxaT, PermissionAxaEnvT[]>;
export type PermissionsByRoleT = Record<RoleT, PermissionT[]>;

/**
 * GET /apibo/authorization/permissions_roles
 */

export const ZodPermission: z.ZodType<PermissionT> = z.object({
  action: createZodArrayable(ZodPermissionActionTWithWildcard),
  resource: ZodPermissionResourceWithWildcard,
});
export const ZodUserPermissionsRolesUser = z.object({
  id: ZodId,
  email: ZodTrimmedString,
  first_name: ZodTrimmedString,
  last_name: ZodTrimmedString,
  locale: ZodTrimmedString,
  picture_url: ZodTrimmedString,
  roles: z.array(ZodRole),
  roles_emails: z.array(ZodSecureEmail),
  can_see_confidential: z.boolean(),
  can_see_closed_employees: z.boolean(),
});
export const ZodRoleDefinitions: z.ZodType<Partial<Record<RoleT, PermissionT[]>>> = z.partialRecord(
  ZodRole,
  z.array(ZodPermission),
);
const ZodUserPermissionsRolesResponse = z.object({
  user: ZodUserPermissionsRolesUser,
  role_definitions: ZodRoleDefinitions,
});

export const getAuthorizationPermissionsRolesEndpoint = endpoint({
  method: 'GET',
  responseBody: ZodUserPermissionsRolesResponse,
});

export type GetAuthorizationPermissionsRolesEndpoint = GetEndpointType<
  typeof getAuthorizationPermissionsRolesEndpoint
>;
